import { z } from "zod";
import { optional } from "./appointments";
import { recordId } from "./contracts";
import { unset } from "./fields";

const count = z.number().int().nonnegative();
const timestamp = z.string().datetime({ offset: true });
const recipientType = z.enum(["individual", "subs"]);
const named = z.object({ id: recordId, name: z.string() });

export const PAYOUT_SCAN_LIMIT_MAX = 100;

export const payoutScanRequest = z.object({
  apply: z.boolean().default(false),
  limit: z.number().int().min(1).max(PAYOUT_SCAN_LIMIT_MAX).default(50),
});
export type PayoutScanRequest = z.input<typeof payoutScanRequest>;

export const storedPayoutStatus = z.enum(["pending", "paid", "failed", "manual", "extra"]);
export type StoredPayoutStatus = z.infer<typeof storedPayoutStatus>;

export const payoutReviewReason = z.enum(["amount_mismatch", "currency_mismatch", "refunded", "not_succeeded"]);
export type PayoutReviewReason = z.infer<typeof payoutReviewReason>;

export const payoutScanOutcome = z.discriminatedUnion("action", [
  z.object({ action: z.literal("updated_to_paid"), markedItems: count }),
  z.object({ action: z.literal("already_settled") }),
  z.object({ action: z.literal("would_update_to_paid") }),
  z.object({ action: z.literal("needs_review"), reason: payoutReviewReason }),
  z.object({ action: z.literal("left_pending") }),
  z.object({ action: z.literal("error"), error: z.string() }),
]);
export type PayoutScanOutcome = z.infer<typeof payoutScanOutcome>;

export const stripePaymentSeen = z.object({ status: z.string(), amountReceived: z.number().int(), currency: z.string() });
export type StripePaymentSeen = z.infer<typeof stripePaymentSeen>;

export const scannedPayout = z.object({
  payoutId: recordId,
  payerOrgId: recordId,
  payerName: z.string().nullable(),
  recipientType,
  recipientId: recordId.nullable(),
  recipientName: z.string().nullable(),
  amount: z.number(),
  currency: z.string(),
  payoutStatus: storedPayoutStatus,
  stripePaymentIntentId: z.string(),
  stripe: stripePaymentSeen.nullable(),
  createdAt: timestamp,
  appointmentItemCount: count,
  dailyWorkItemCount: count,
  outcome: payoutScanOutcome,
});
export type ScannedPayout = z.infer<typeof scannedPayout>;

export const payoutScan = z.object({
  apply: z.boolean(),
  limit: count,
  scanned: count,
  updated: count,
  checkedAt: timestamp,
  results: z.array(scannedPayout),
});
export type PayoutScan = z.infer<typeof payoutScan>;

export const UNPAID_INVOICE_LIMIT_MAX = 1000;

export const MARK_INVOICES_PAID_MAX = 400;

const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Use a YYYY-MM month.");

export const unpaidInvoiceQuery = z.object({
  fromMonth: optional(month),
  toMonth: optional(month),
  limit: z.preprocess(unset, z.coerce.number().int().min(1).max(UNPAID_INVOICE_LIMIT_MAX).default(500)),
});
export type UnpaidInvoiceQuery = z.infer<typeof unpaidInvoiceQuery>;

export const storedInvoiceStatus = z.enum(["draft", "sent", "approved", "partial", "paid", "void"]);
export type StoredInvoiceStatus = z.infer<typeof storedInvoiceStatus>;

export const unpaidInvoice = z.object({
  invoiceId: recordId,
  invoiceNumber: z.number().int(),
  legacyInvoiceId: z.string().nullable(),
  amount: z.number(),
  status: storedInvoiceStatus,
  issueDate: timestamp,
  dueDate: timestamp,
  paidAt: timestamp.nullable(),
});
export type UnpaidInvoice = z.infer<typeof unpaidInvoice>;

export const unpaidInvoiceList = z.object({ fromMonth: month, toMonth: month, limit: count, items: z.array(unpaidInvoice) });
export type UnpaidInvoiceList = z.infer<typeof unpaidInvoiceList>;

export const markInvoicesPaidRequest = z.object({ invoiceIds: z.array(recordId.toLowerCase()).min(1).max(MARK_INVOICES_PAID_MAX) });
export type MarkInvoicesPaidRequest = z.infer<typeof markInvoicesPaidRequest>;

export const invoicePaidOutcome = z.enum(["updated_to_paid", "already_paid", "skipped_void", "not_found"]);
export type InvoicePaidOutcome = z.infer<typeof invoicePaidOutcome>;

export const markedInvoice = z.object({ invoiceId: recordId, action: invoicePaidOutcome, status: storedInvoiceStatus.nullable(), paidAt: timestamp.nullable() });
export type MarkedInvoice = z.infer<typeof markedInvoice>;

export const markedInvoicesPaid = z.object({ requested: count, updated: count, checkedAt: timestamp, results: z.array(markedInvoice) });
export type MarkedInvoicesPaid = z.infer<typeof markedInvoicesPaid>;

export const AUTOPAY_RUN_PAGE_SIZE_MAX = 100;

export const autopayRunListQuery = z.object({
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(AUTOPAY_RUN_PAGE_SIZE_MAX).default(20)),
});
export type AutopayRunListQuery = z.infer<typeof autopayRunListQuery>;

export const adminAutopayRun = z.object({
  id: recordId,
  vendor: named,
  runDate: z.string().date(),
  timezone: z.string(),
  status: z.string(),
  startedAt: timestamp,
  finishedAt: timestamp.nullable(),
  summary: z.record(z.string(), z.unknown()).nullable(),
});
export type AdminAutopayRun = z.infer<typeof adminAutopayRun>;

export const adminAutopayRunPage = z.object({ data: z.array(adminAutopayRun), total: count, page: count, pageSize: count, totalPages: count });
export type AdminAutopayRunPage = z.infer<typeof adminAutopayRunPage>;

export const adminAutopayRunItem = z.object({
  id: recordId,
  outcome: z.string(),
  amountCents: z.number().int().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: timestamp,
  recipientType,
  recipientName: z.string(),
  enrollmentId: recordId,
});
export type AdminAutopayRunItem = z.infer<typeof adminAutopayRunItem>;

export const adminAutopayRunItemList = z.object({ items: z.array(adminAutopayRunItem) });
export type AdminAutopayRunItemList = z.infer<typeof adminAutopayRunItemList>;

export const adminAutopayEnrollment = z.object({
  id: recordId,
  vendor: named,
  recipientType,
  recipientName: z.string(),
  pausedAt: timestamp.nullable(),
  pauseReason: z.string().nullable(),
  consecutiveFailures: count,
  lastRunAt: timestamp.nullable(),
  lastRunOutcome: z.string().nullable(),
  lastRunError: z.string().nullable(),
});
export type AdminAutopayEnrollment = z.infer<typeof adminAutopayEnrollment>;

export const adminAutopayEnrollmentList = z.object({ items: z.array(adminAutopayEnrollment) });
export type AdminAutopayEnrollmentList = z.infer<typeof adminAutopayEnrollmentList>;
